<template>
  <b-container fluid class="home-padding">
    <HeroBanner :experience-type="getDesign">
      <DesignBannerContent :suggested-searches="suggestedSearches" />
    </HeroBanner>

    <Sector v-if="listExist('home_browse_niches', getDesign)">
      <Subtitle>
        <template #text>{{ $t('home.design.sector.title1') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="$urlBuilder('topics-home')"
            >{{ $t('home.design.sector.seeAll1') }}</Button
          >
        </template>
      </Subtitle>
      <Carousel
        :items="list('home_browse_niches', getDesign)"
        :height="carouselHeight"
        :margin-right="15"
        :margin-bottom="10"
        :see-more-enabled="true"
        :border-radius="10"
        :see-more-link="$urlBuilder('topics-home')"
        :show-shadow="true"
        :show-bottom-bar="true"
        :show-bottom-see-more="false"
        :is-grouper="true"
      >
        <template #bottomBar="slotProps">
          <CollectionsBottomBar :show-see-more="false" :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <YouCanFind />
    </Sector>
    <Sector v-if="listExist('home_featured', getDesign)">
      <Subtitle>
        <template #text>{{ $t('home.design.sector.title2') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="$trailingSlash(localePath('design-featured-graphics'))"
            >{{ $t('home.design.sector.seeAll2') }}</Button
          >
        </template>
      </Subtitle>
      <Carousel
        :items="list('home_featured', getDesign)"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('design-featured-graphics'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <TextImageBanner
        link="new-vexels"
        :margin-vertical="40"
        svg-icon="bringyourideastolife2"
        :title="$t('home.design.imageBanner.title1')"
        :subtitle="$t('home.design.imageBanner.subtitle1')"
        :button-text="$t('home.design.imageBanner.button1')"
      >
        <div class="woman-sizing-container">
          <picture>
            <source
              srcset="https://cdn.vexels.com/img/woman-computer-lottie-replace.png?fmt=webp&w=300"
              type="image/webp"
              media="(max-width: 992px)"
            />
            <source
              srcset="https://cdn.vexels.com/img/woman-computer-lottie-replace.png?fmt=webp"
              type="image/webp"
            />
            <source
              srcset="https://cdn.vexels.com/img/woman-computer-lottie-replace.png"
              type="image/png"
            />
            <img
              class="img-fluid"
              src="https://cdn.vexels.com/img/woman-computer-lottie-replace.png"
              :alt="$t('home.design.sector.womanUsingComputerAlt')"
              width="1280"
              height="720"
              loading="lazy"
            />
          </picture>
        </div>
      </TextImageBanner>
    </Sector>
    <Sector>
      <LogoMakerBanner />
    </Sector>
    <Sector v-if="listExist('home_transparent_pngs', getDesign)">
      <Subtitle>
        <template #text>{{ $t('home.design.sector.title3') }}</template>
        <template #side
          ><EditableColorsIcons :see-more-text="$t('home.design.sector.seeAll3')"
        /></template>
      </Subtitle>
      <Carousel
        :items="list('home_transparent_pngs', getDesign)"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('svg-png'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <TextImageBanner
        link="design-latest-vectors"
        :image-first="true"
        svg-icon="bringyourideastolife1"
        :title="$t('home.design.imageBanner.title2')"
        :subtitle="$t('home.design.imageBanner.subtitle2')"
        :button-text="$t('home.design.imageBanner.button2')"
      >
        <div class="friends-sizing-container">
          <picture>
            <source
              srcset="https://cdn.vexels.com/img/friends-web-lottie-replace.png?fmt=webp&w=300"
              type="image/webp"
              media="(max-width: 992px)"
            />
            <source
              srcset="https://cdn.vexels.com/img/friends-web-lottie-replace.png?fmt=webp"
              type="image/webp"
            />
            <source
              srcset="https://cdn.vexels.com/img/friends-web-lottie-replace.png"
              type="image/png"
            />
            <img
              class="img-fluid"
              src="https://cdn.vexels.com/img/friends-web-lottie-replace.png"
              :alt="$t('home.design.sector.websiteDesignAlt')"
              width="100%"
              height="100%"
              loading="lazy"
            />
          </picture>
        </div>
      </TextImageBanner>
    </Sector>
    <Sector v-if="listExist('home_templates', getDesign)">
      <Subtitle>
        <template #text>{{ $t('home.design.sector.title4') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="$urlBuilder('landing', { uid: 'template-designs' })"
            >{{ $t('home.design.sector.seeAll4') }}</Button
          >
        </template>
      </Subtitle>
      <Carousel
        :items="list('home_templates', getDesign)"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$urlBuilder('landing', { uid: 'template-designs' })"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <MockupSection
        :title="$t('home.design.sector.title5')"
        color-img="https://cdn.vexels.com/img/color-laptop.jpg"
        :color-img-alt="$t('home.design.sector.colouredLaptopMockupAlt')"
        white-img="https://cdn.vexels.com/img/white-laptop.jpg"
        :white-img-alt="$t('home.design.sector.whiteLaptopMockupAlt')"
        color="#3f80f1"
        :reserved-width="927"
        :reserved-height="606"
      >
        <template #action-btn>
          <div class="button-mockup col-12 col-sm-6 justify-content-end">
            <Button class="mr-3" shape="full" :to="$urlBuilder('landing', { uid: 'psd-mockups' })">
              {{ $t('home.design.sector.button1') }}
            </Button>
          </div>
          <div class="button-mockup col-12 col-sm-6 justify-content-start">
            <Button class="ml-3" shape="full" to="https://mockups.vexels.com/" :blank="true">
              {{ $t('home.design.sector.button2') }}
            </Button>
          </div>
        </template>
      </MockupSection>
    </Sector>
    <Sector v-if="listExist('home_mockups', getDesign)">
      <Subtitle>
        <template #text>{{ $t('home.design.sector.title5') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="
              $urlBuilder('search', {
                path: 'psd',
                keyword: 'mockup'
              })
            "
            >{{ $t('home.design.sector.seeAll5') }}</Button
          >
        </template>
      </Subtitle>
      <Carousel
        :items="list('home_mockups', getDesign)"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="
          $urlBuilder('search', {
            path: 'psd',
            keyword: 'mockup'
          })
        "
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector v-if="listExist('home_collections', getDesign)">
      <Subtitle :margin-right="5">
        <template #text>{{ $t('home.design.sector.title6') }}</template>
        <template #side><BadgeNew :height="20" /></template>
      </Subtitle>
      <Carousel
        :items="list('home_collections', getDesign)"
        :height="carouselCollectionHeight"
        :margin-bottom="45"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('collections'))"
        :show-bottom-bar="true"
        :is-grouper="true"
        :is-collection="true"
      >
        <template #bottomBar="slotProps">
          <CollectionsBottomBar :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <CantFind
        :title="$t('home.design.cantFind.title')"
        :description="$t('home.design.cantFind.description')"
        :alt-image="$t('home.design.cantFind.altImage')"
        :request-text="
          $auth.loggedIn ? $t('home.design.cantFind.request') : $t('home.design.cantFind.learnMore')
        "
      />
    </Sector>
    <Sector>
      <TrustedBy />
    </Sector>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';

import Button from 'UI/Button';
import Carousel from '@/components/Common/Carousel';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import CollectionsBottomBar from '@/components/Collections/CollectionsBottomBar';
import BadgeNew from 'UI/Badges/New';
import MockupSection from '@/components/Home/MockupSection';
import EditableColorsIcons from '@/components/Home/Design/EditableColorsIcons';
import TextImageBanner from '@/components/Home/Design/TextImageBanner';
import LogoMakerBanner from '@/components/Home/Design/LogoMakerBanner';
import CantFind from '@/components/Home/CantFind';
import TrustedBy from '@/components/Common/TrustedBy';
import Subtitle from '@/components/Home/Subtitle';
import Sector from '@/components/Home/Sector';
import YouCanFind from '@/components/Home/Design/YouCanFind';
import DesignBannerContent from '@/components/Home/Design/DesignBannerContent';
import HeroBanner from '@/components/Home/HeroBanner';

export default {
  components: {
    Carousel,
    NormalVectorOverlay,
    DesignBannerContent,
    HeroBanner,
    Sector,
    Subtitle,
    Button,
    EditableColorsIcons,
    CollectionsBottomBar,
    BadgeNew,
    TextImageBanner,
    LogoMakerBanner,
    CantFind,
    TrustedBy,
    MockupSection,
    YouCanFind
  },
  data() {
    return {
      carouselHeight: 180,
      carouselCollectionHeight: 230
    };
  },
  async fetch() {
    const design = this.$store.getters['experience/getDesign'];
    const home_browse_niches = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_browse_niches',
      type: 'topics',
      exp: design
    });
    const home_featured = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_featured',
      type: 'preview',
      exp: design
    });
    const home_transparent_pngs = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_transparent_pngs',
      type: 'preview',
      exp: design
    });
    const home_templates = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_templates',
      type: 'preview',
      exp: design
    });
    const home_mockups = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_mockups',
      type: 'preview',
      exp: design
    });
    const home_collections = this.$store.dispatch('listings/GET_LIST', {
      name: 'home_collections',
      type: 'collection',
      exp: design
    });

    await Promise.all([
      home_browse_niches,
      home_featured,
      home_transparent_pngs,
      home_templates,
      home_mockups,
      home_collections
    ]);
  },
  computed: {
    ...mapGetters('listings', ['list', 'listExist']),
    ...mapGetters('experience', ['getDesign'])
  },
  methods: {
    async vectorClick({ item, shownListing }, e) {
      e.preventDefault();
      this.$bvModal.show('preview-vector-modal');
      await this.$store.dispatch('vectors/OPEN_PREVIEW', { item, list: shownListing });
    }
  }
};
</script>

<style scoped>
.woman-sizing-container {
  padding: 0 15%;
}
.friends-sizing-container {
  padding: 0 10%;
}
</style>
